import { getStoreInfo } from './api';

/**
 * v5 — Service API pour l'UTILISATION (redeem) des points de fidélité.
 *
 * 2 endpoints :
 *  1. get_redeemable_info : vérifier les points et l'autorisation kiosk
 *  2. redeem_loyalty_points : déduire les points APRÈS paiement confirmé
 *
 * Tous les appels passent par https://api.restomi.fr/v3/api/store/...
 * Headers requis : storeid, token, type:0
 */

const API_BASE = 'https://api.restomi.fr/v3/api/store';

export interface RedeemableInfo {
  success: boolean;
  found: boolean;
  customer?: {
    _id?: string;
    name?: string;
    phone?: string;
  };
  loyalty_points: number;
  max_points_usable: number;
  max_discount: number;
  redeem_step?: number;
  can_redeem: boolean;
  channel_allowed: boolean;
  reason?: string;
  setting?: any;
  message?: string;
}

export interface RedeemResult {
  success: boolean;
  points_used?: number;
  discount?: number;
  new_balance?: number;
  message?: string;
}

export interface ApplyDiscountResult {
  success: boolean;
  old_total?: number;
  discount?: number;
  new_total?: number;
  /** v5: nb points déduits (apply_loyalty_discount déduit maintenant les points atomiquement) */
  points_used?: number;
  /** v5: solde restant après déduction */
  new_balance?: number;
  /** v5: raison de l'échec (ex: "points_already_used") */
  reason?: string;
  message?: string;
}

/**
 * Construit les headers requis pour les appels store/loyalty.
 */
function buildHeaders(): HeadersInit | null {
  const storeInfo = getStoreInfo();
  if (!storeInfo) return null;
  return {
    'Content-Type': 'application/json',
    storeid: storeInfo.store_id,
    token: storeInfo.server_token,
    type: '0',
  };
}

/**
 * Vérifie les points + l'autorisation pour le canal kiosk.
 * À appeler quand l'user a saisi un téléphone et clique "Continuer vers le paiement".
 *
 * IMPORTANT : afficher l'option redeem SEULEMENT si :
 *   found === true ET channel_allowed === true ET can_redeem === true
 */
export async function fetchRedeemableInfo(
  phone: string,
  orderAmount: number
): Promise<RedeemableInfo | null> {
  try {
    const headers = buildHeaders();
    if (!headers) {
      console.warn('[LoyaltyRedeem] Store info non disponible');
      return null;
    }
    const body = {
      phone,
      order_amount: orderAmount,
      channel: 'kiosk',
    };
    console.log('[LoyaltyRedeem] get_redeemable_info →', body);
    const response = await fetch(`${API_BASE}/get_redeemable_info`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      console.warn('[LoyaltyRedeem] get_redeemable_info HTTP', response.status);
      return null;
    }
    const data: RedeemableInfo = await response.json();
    console.log('[LoyaltyRedeem] get_redeemable_info ←', data);
    return data;
  } catch (e) {
    console.error('[LoyaltyRedeem] fetchRedeemableInfo error', e);
    return null;
  }
}

/**
 * Applique la réduction fidélité sur l'order_payment AVANT le paiement.
 *
 * v5 NOUVEAU COMPORTEMENT ATOMIQUE : cette fonction fait TOUT en une seule opération :
 *  - réduit order_payment.total (+ user_pay_payment + remaining_payment)
 *  - déduit les points du solde client (atomique pour bloquer double-utilisation)
 *
 * À appeler APRÈS get_order_cart_invoice (qui retourne order_payment_id)
 * et AVANT pay_order_payment.
 *
 * ⚠️ Ne plus appeler redeem_loyalty_points après le paiement, c'est inclus ici.
 *
 * Gestion des erreurs :
 *  - reason="points_already_used" : un autre kiosk a déjà utilisé ces points
 *    → afficher un message au client + continuer la commande SANS réduction
 *
 * @param orderPaymentId L'_id de l'order_payment retourné par get_order_cart_invoice
 * @param loyaltyDiscount Montant de la réduction en € (ex: 10.00)
 * @param loyaltyPointsUsed Nombre de points utilisés
 * @param phone Téléphone du client (formaté). REQUIS pour identifier le client à déduire.
 * @param note Note descriptive optionnelle (ex: "Réduction fidélité -200 pts = -10€")
 */
export async function applyLoyaltyDiscount(
  orderPaymentId: string,
  loyaltyDiscount: number,
  loyaltyPointsUsed: number,
  phone: string,
  note?: string
): Promise<ApplyDiscountResult> {
  try {
    const headers = buildHeaders();
    if (!headers) {
      return { success: false, message: 'Store info non disponible' };
    }
    const body: any = {
      order_payment_id: orderPaymentId,
      loyalty_discount: loyaltyDiscount,
      loyalty_points_used: loyaltyPointsUsed,
      phone,
    };
    if (note) body.note = note;

    console.log('[LoyaltyRedeem] apply_loyalty_discount →', body);
    const response = await fetch(`${API_BASE}/apply_loyalty_discount`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const txt = await response.text();
      console.warn('[LoyaltyRedeem] apply_loyalty_discount HTTP', response.status, txt);
      return { success: false, message: `HTTP ${response.status}` };
    }
    const data: ApplyDiscountResult = await response.json();
    console.log('[LoyaltyRedeem] apply_loyalty_discount ←', data);
    return data;
  } catch (e) {
    console.error('[LoyaltyRedeem] applyLoyaltyDiscount error', e);
    return { success: false, message: 'Network error' };
  }
}

/**
 * @deprecated v5 — NE PLUS UTILISER. apply_loyalty_discount déduit maintenant les
 * points atomiquement au moment de l'application (pour bloquer la double-utilisation
 * sur 2 commandes simultanées). Appeler ceci en plus créerait une double déduction.
 *
 * Conservé uniquement pour rétrocompatibilité si un autre appelant l'utilise encore.
 */
export async function redeemLoyaltyPoints(
  phone: string,
  pointsToUse: number,
  orderAmount: number,
  orderReference: string | number
): Promise<RedeemResult> {
  try {
    const headers = buildHeaders();
    if (!headers) {
      return { success: false, message: 'Store info non disponible' };
    }
    const body = {
      phone,
      points_to_use: pointsToUse,
      order_amount: orderAmount,
      order_reference: String(orderReference),
      channel: 'kiosk',
    };
    console.log('[LoyaltyRedeem] redeem_loyalty_points →', body);
    const response = await fetch(`${API_BASE}/redeem_loyalty_points`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const txt = await response.text();
      console.warn('[LoyaltyRedeem] redeem_loyalty_points HTTP', response.status, txt);
      return { success: false, message: `HTTP ${response.status}` };
    }
    const data: RedeemResult = await response.json();
    console.log('[LoyaltyRedeem] redeem_loyalty_points ←', data);
    return data;
  } catch (e) {
    console.error('[LoyaltyRedeem] redeemLoyaltyPoints error', e);
    return { success: false, message: 'Network error' };
  }
}

/**
 * Génère les paliers à proposer à l'utilisateur.
 * Algo : [step, 2*step, ..., max_points_usable]
 * - max 4 paliers (3 réguliers + 1 "Max" exact)
 * - n'affiche pas un palier s'il dépasse max_points_usable
 *
 * Exemple : step=100, max=350 → [100, 200, 300, max=350]
 *           step=100, max=300 → [100, 200, max=300]
 *           step=50,  max=100 → [50, max=100]
 */
export interface RedeemTier {
  points: number;
  discount: number;
  isMax: boolean;
}

export function buildRedeemTiers(info: RedeemableInfo): RedeemTier[] {
  const step = info.redeem_step || 100;
  const max = info.max_points_usable;
  const maxDiscount = info.max_discount;

  if (!max || max <= 0 || !step || step <= 0) return [];

  // Calcul du ratio €/pt à partir des données API
  const ratio = maxDiscount / max; // ex: 22/350 ≈ 0.0628 €/pt

  const tiers: RedeemTier[] = [];
  let current = step;
  // 3 paliers réguliers max + le palier Max
  while (tiers.length < 3 && current < max) {
    tiers.push({
      points: current,
      discount: parseFloat((current * ratio).toFixed(2)),
      isMax: false,
    });
    current += step;
  }
  // Toujours ajouter le palier "Max" si différent des paliers déjà ajoutés
  if (tiers.length === 0 || tiers[tiers.length - 1].points < max) {
    tiers.push({
      points: max,
      discount: parseFloat(maxDiscount.toFixed(2)),
      isMax: true,
    });
  }
  return tiers;
}
